import i18next from 'i18next'
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import {
  Button, ButtonGroup,
  Card, Divider, Popover,
  Switch,
  Tab, Tabs, TextArea, Tooltip
} from '@blueprintjs/core'
import { defer, truncate } from 'lodash'
import { InsertFormClass } from '../classes'
import { hasScrollbarAlways } from '../../../../../helper/CSSclasses'
import { OverlayProps, sidebarDirection } from '../../../../../helper/configs'
import { BlockMath, InlineMath } from 'react-katex'
import MathQuill, { addStyles as addMathquillStyles } from 'react-mathquill'
import { copyTextToClipBoard } from '../../../../../helper/functions'
import { showToastByMenuDirectionBase } from '../../../../commonInterface/toast'
import insertTextAtCursor from 'insert-text-at-cursor'
import { globalOnDemandVars } from '../../../../../helper/context'
import { css as classname } from 'emotion'
import { useTranslation } from 'react-i18next'
addMathquillStyles()

// 위키에서 가져올때 ctrl+r로 바꾸기할 regex (space 포함)
// \\[^\\]*\n\{\\.*}
// -----------------------------------------------------------------------------------------------------------
// (\\[^\\]*) + space2
// -> '\\$1', + space1

const divider = 'divider'
const LaTeXItems = {
  'basic': [
    '\\frac{a}{b}', '\\sqrt{a}', '\\sqrt[n]{a}', '\\log_{a}{b}', '\\lg{a}', 'a^{b}', 'a_{b}', 'c_{a}^{b}',
    divider,
    '\\infty', '\\therefore', '\\because', '\\lfloor', '\\rfloor', '\\lceil', '\\rceil'
  ],
  'sort of layout': [
    '\\left(\\begin{array}{c}a\\\\ b\\end{array}\\right)',
    '\\begin{bmatrix}a & b \\\\c & d \\end{bmatrix}',
    '\\begin{cases}a & x = 0\\\\b & x > 0\\end{cases}',
    divider,
    '\\left(\\right)', '\\left[\\right]', '\\left\\{\\right\\}', '\\langle', '\\rangle'
  ],
  'letters 1': ['\\alpha', '\\beta', '\\gamma', '\\delta', '\\epsilon', '\\zeta', '\\eta', '\\theta', '\\iota', '\\kappa', '\\lambda', '\\mu', '\\nu', '\\xi ', '\\pi', '\\rho', '\\sigma', '\\tau', '\\upsilon', '\\phi', '\\chi', '\\psi', '\\omega',
    '\\varepsilon', '\\vartheta', '\\varkappa', '\\varpi', '\\varrho', '\\varphi', '\\varsigma', '\\Gamma', '\\Delta', '\\Theta', '\\Lambda', '\\Upsilon', '\\Xi', '\\Phi', '\\Pi', '\\Psi', '\\Sigma', '\\Omega', '\\digamma', '\\aleph', '\\beth', '\\gimel', '\\daleth',
    '\\Pi', '\\Psi', '\\Sigma', '\\Omega', '\\digamma', '\\aleph', '\\beth', '\\gimel', '\\daleth',
    '\\forall', '\\exists', '\\nexists', '\\Finv', '\\Game', '\\backepsilon', '\\imath', '\\jmath', '\\ell', '\\Bbbk', '\\amalg', '\\nabla', '\\mho', '\\partial', '\\eth', '\\hbar', '\\hslash', '\\Im', '\\Re', '\\wp', '\\emptyset', '\\varnothing', '\\complement', '\\circledS'
  ],
  'letters 2': [
    // katex랑 mathQuill이 서로 안맞는게 있을때 []로 하고 0번째 인덱스에 mathQuill 용, 1번째 인덱스에 katex 용 string을 넣기로 함
    ['\\N', '\\mathbb{N}'], ['\\Z', '\\mathbb{Z}'], ['\\Q', '\\mathbb{Q}'], ['\\R', '\\mathbb{R}'], ['\\C', '\\mathbb{C}'], ['\\P', '\\mathbb{P}'], divider,
    '\\acute{a}', '\\hat{a}', '\\ddot{a}', '\\bar{a}', '\\tilde{a}', '\\grave{a}', '\\check{a}', '\\dot{a}', '\\breve{a}', '\\vec{a}',
    divider, '\\mathcal{A}', '\\mathfrak{A}', '\\mathsf{A}', '\\mathbf{A}', '\\textbf{A}', '\\mathbb{A}'
  ],
  logic: [
    '\\neq', '\\equiv', '\\approx', '\\doteq', '\\cong', '\\simeq', '\\sim', '\\propto',
    '\\leq', '\\geq', '\\equiv',
    divider,
    '\\in', '\\notin', '\\ni', '\\subset', '\\subseteq', '\\supset', '\\supseteq', '\\cup', '\\cap', '\\setminus',
    '\\bigcap_{a}^{b}', '\\bigcup_{a}^{b}',
    divider,
    '\\wp', '\\emptyset', '\\exists', '\\exists!', '\\nexists', '\\forall', '\\lor', '\\land', '\\neg',
    divider,
    '\\to', '\\gets', '\\longleftrightarrow', '\\implies', '\\Longleftarrow', '\\iff'
  ],
  operators: [
    '\\times', '\\div', '\\ast', '\\bullet', '\\pm', '\\mp',
    divider,
    '\\int_{a}^{b}', '\\iint_{a}^{b}', '\\oint_{a}^{b}', divider, '\\frac{\\text{d}x}{\\text{d}y}', '\\frac{\\partial a}{\\partial b}', '\\text{d}x', '\\partial a',
    divider,
    '\\sum_{a}^{b}', '\\prod_{a}^{b}', '\\coprod_{a}^{b}',
    divider,
    '\\lim_{a \\rightarrow b}',
    divider,
    '\\odot', '\\oplus', '\\ominus', '\\otimes', '\\oslash', '\\circledcirc', '\\circleddash', '\\circledast', '\\bigcirc', '\\divideontimes', '\\boxdot', '\\boxplus', '\\boxminus', '\\boxtimes', '\\Box',
    divider,
    '\\bigcap', '\\bigwedge', '\\bigcup', '\\bigvee', '\\bigsqcup', '\\biguplus', '\\bigoplus', '\\bigotimes', '\\bigodot'
  ],
  geometry: [
    '\\overline{AB}', '\\overrightarrow{AB}', '\\angle', '\\measuredangle', '\\triangle', '\\square', '\\cong', '\\ncong', '\\sim', '\\nsim', '\\|', '\\nparallel', '\\perp', '\\not\\perp',
    divider,
    '\\theta', divider, '\\sin', '\\cos', '\\tan', '\\sec', '\\csc', '\\cot', '\\arcsin', '\\arccos', '\\arctan', '\\sinh', '\\cosh', '\\tanh', '\\coth'

  ],
  orders: [
    '<', '>',
    '\\leq', '\\leqq', '\\geq', '\\geqq', '\\geqslant', '\\nless', '\\nleq', '\\lneq', '\\nleqq', '\\lneqq', '\\lvertneqq', '\\nleqslant', '\\ngtr', '\\ngeq', '\\gneq', '\\ngeqq', '\\gneqq', '\\gvertneqq', '\\ngeqslant', '\\vartriangleleft', '\\trianglelefteq', '\\lesssim', '\\lessapprox', '\\prec', '\\preceq', '\\precsim', '\\precapprox', '\\vartriangleright', '\\trianglerighteq', '\\gtrsim', '\\gtrapprox', '\\succ', '\\succeq', '\\succsim', '\\succapprox', '\\ntriangleleft', '\\ntrianglelefteq', '\\lnsim', '\\lnapprox', '\\nprec', '\\npreceq', '\\precnsim', '\\precnapprox', '\\ntriangleright', '\\ntrianglerighteq', '\\gnsim', '\\gnapprox', '\\nsucc', '\\nsucceq', '\\succnsim', '\\succnapprox', '\\eqslantless', '\\lessgtr', '\\lesseqgtr', '\\lesseqqgtr', '\\eqslantgtr', '\\gtrless', '\\gtreqless', '\\gtreqqless', '\\ll', '\\lll', '\\lessdot', '\\preccurlyeq', '\\curlyeqprec', '\\gg', '\\ggg', '\\gtrdot', '\\succcurlyeq', '\\curlyeqsucc'
  ],
  arrows: [
    '\\leftarrow', '\\Leftarrow', '\\leftharpoonup', '\\leftharpoondown', '\\leftleftarrows', '\\rightarrow', '\\Rightarrow', '\\rightharpoonup', '\\rightharpoondown', '\\rightrightarrows', '\\uparrow', '\\Uparrow', '\\upharpoonleft', '\\upharpoonright', '\\upuparrows', '\\downarrow', '\\Downarrow', '\\downharpoonleft', '\\downharpoonright', '\\downdownarrows', '\\longleftarrow', '\\Longleftarrow', '\\hookleftarrow', '\\leftrightharpoons', '\\leftrightarrows', '\\Lleftarrow', '\\Lsh', '\\leftarrowtail', '\\twoheadleftarrow', '\\curvearrowleft', '\\circlearrowleft', '\\looparrowleft', '\\longrightarrow', '\\Longrightarrow', '\\hookrightarrow', '\\rightleftharpoons', '\\rightleftarrows', '\\Rrightarrow', '\\Rsh', '\\rightarrowtail', '\\twoheadrightarrow', '\\curvearrowright', '\\circlearrowright', '\\looparrowright', '\\leftrightarrow', '\\longleftrightarrow', '\\updownarrow', '\\nleftarrow', '\\nrightarrow', '\\nleftrightarrow', '\\Leftrightarrow', '\\Longleftrightarrow', '\\Updownarrow', '\\nLeftarrow', '\\nRightarrow', '\\nLeftrightarrow', '\\mapsto', '\\longmapsto', '\\multimap', '\\rightsquigarrow', '\\leftrightsquigarrow', '\\swarrow', '\\searrow', '\\nwarrow', '\\nearrow'
  ]
}
const LaTeXItemsKeys = Object.keys(LaTeXItems)

const _renderError = (error) => {
  const string = `${error.name}${'position' in error ? `: check position ${error.position} in $ $, or if you want to use just $, replace $ with \\$` : ''}`
  return (
    <Tooltip content={string} {...OverlayProps}>
      <span className={css`background-color: var(--gray3);`}>(LaTeX Error)</span>
    </Tooltip>
  )
}
const MathRenderer = props => <BlockMath
  errorColor={'var(--gray3);'}
  renderError={_renderError}
>{props.value}</BlockMath>
const InlineMathRenderer = props => <InlineMath
  errorColor={'var(--gray3);'}
  renderError={_renderError}
>{props.value}</InlineMath>

const GeneratingFuncForLaTeXStringToButton = (MathQuillRef) =>
  (LaTexString) => {
    let forKatex, forMathQuill
    if (Array.isArray(LaTexString)) {
      forMathQuill = LaTexString[0]
      forKatex = LaTexString[1]
    }
    return (LaTexString === divider ? <Divider />
      : <Button icon={<span css={css`font-size: 20px;`}><InlineMathRenderer value={forKatex || LaTexString} /></span>} key={LaTexString} onMouseDown={() => {
        const activeElement = document.activeElement
        // console.log(activeElement.tagName)
        if (activeElement && (activeElement.tagName.toLowerCase() === 'input' ||
          activeElement.tagName.toLowerCase() === 'textarea')) {
        // console.log('1')
          if (activeElement.parentElement.parentElement === MathQuillRef.current.mathField.el()) {
          // console.log('1-1')
            MathQuillRef.current.mathField.write(forMathQuill || LaTexString)
            defer(() => {
              MathQuillRef.current.mathField.focus()
            })
          // console.log('1-1-2')
          } else {
            const originalCursorPosition = activeElement.selectionStart
            insertTextAtCursor(activeElement, forKatex || LaTexString)
            defer(() => {
              activeElement.focus()
              activeElement.setSelectionRange(originalCursorPosition, originalCursorPosition)
            })
          // console.log('1-2')
          }
        }
      }} />
    )
  }
// -----------------------------------------------------------------------------------------------------------

function EditorBase (forDialog, { contentData, setContentData }) {
  const { t } = useTranslation()

  if (process.env.NODE_ENV !== 'production') {
    console.tron.display({
      name: 'EditorBig',
      value: {
        contentData
      }
    // , important: true
    })
  }

  const MathQuillRef = useRef(null)

  // useEffect(() => {
  //   if (MathQuillRef.current && MathQuillRef.current.mathField) {
  // MathQuillRef.current.mathField.latex(contentData || '')
  //   }
  // }, [contentData])

  const [tabId, setTabId] = useState(LaTeXItemsKeys[0])

  const LaTeXStringToButton = useCallback(
    GeneratingFuncForLaTeXStringToButton(MathQuillRef),
    []
  )

  const MouseSelector = useMemo(() => (
    <Card css={css`overflow: auto; position: relative; flex: none; padding: 0 0 10px 4px;`}
      className={hasScrollbarAlways}>
      <Tabs id={'latexTabs'} selectedTabId={tabId} onChange={(newTabId) => {
        setTabId(newTabId)
      }} renderActiveTabPanelOnly>{
          LaTeXItemsKeys.map((key) => (
            <Tab title={key} id={key} key={key} panel={
              <ButtonGroup css={css`flex-wrap: wrap;`}>{
                LaTeXItems[key].map(LaTeXStringToButton)
              }</ButtonGroup>
            } />
          ))
        }
      </Tabs>
    </Card>
  ), [LaTeXStringToButton, tabId])

  const [useMathQuill, setUseMathQuill] = useState(true)

  const [textValue, setTextValue] = useState(contentData)
  const source = useRef('visual')
  //
  // useEffect(() => {
  //   if (useMathQuill) { MathQuillRef.current.mathField.latex(contentData || '') }
  // }, [contentData, useMathQuill])

  const copiedContentData = useRef(contentData)

  useEffect(() => {
    if (!contentData) {
      MathQuillRef.current.mathField.latex('')
      setTextValue('')
    } else if (contentData !== copiedContentData.current) {
      MathQuillRef.current.mathField.latex(contentData)
      setTextValue(contentData)
      copiedContentData.current = contentData
    }
  }, [contentData, textValue, useMathQuill])

  const mathQuill = useMemo(() => (
    <MathQuill
      // latex prop 은 그저 initialValue 일 뿐임
      latex={useMathQuill ? contentData : ''}
      onChange={(mathField) => {
        if (useMathQuill && source.current !== 'text') {
          const latex = mathField.latex()
          copiedContentData.current = latex
          setContentData(latex)
          setTextValue(latex)
        }
      }}
      config={{
        spacesBehavesLikeTab: true,
        restrictMismatchedBrackets: true,
        supSubsRequireOperand: true
      }}
      css={css`width: 100%; height: 100%; position: absolute;`}
      mathquillDidMount={(mathField) => {
        MathQuillRef.current = {
          mathField: mathField
          // latex: mathField.latex,
          // write: mathField.write
          // element: mathField.el()
        }
      }}
    />
  ), [contentData, setContentData, useMathQuill])
  const textEditor = useMemo(() => (
    <TextArea
      value={textValue}
      onChange={(e) => {
        if (source.current !== 'visual') {
          copiedContentData.current = e.target.value
          setContentData(e.target.value)
          setTextValue(e.target.value)
          MathQuillRef.current.mathField.latex(e.target.value || '')
        }
        // if (useMathQuill) { MathQuillRef.current.mathField.latex(e.target.value) }
      }}
      onFocus={() => { source.current = 'text' }}
      spellCheck={false}
      placeholder={t('Raw LaTeX')}
      className={hasScrollbarAlways}
      css={css`position: absolute; top: 30px; width: calc(100% - 6px); min-height: calc(100% - 36px); margin: 2px; resize: none;`}
    />
  ), [setContentData, t, textValue])
  return (
    <div css={css`
    display: flex; flex-direction: column; background-color: white;
    padding: 4px;
    ${forDialog ? 'height: 100%; width: 100%; position: absolute;' : 'height: var(--s7); width: var(--s7);'}
`}>
      {
        forDialog ? <div css={css`max-height: 60%;`} className={hasScrollbarAlways}>{MouseSelector}</div> : (
          <Popover {...OverlayProps}
            position={sidebarDirection === 'right' ? 'left-top' : 'right-top'}
            fill interactionKind={'click-target'}
            targetClassName={classname`width: 100%;`}
            target={<Button icon={'hand-up'} title={t('Select with mouse')} fill />}
            content={<div css={css`max-width: 50vw; max-height: 50vh; overflow: auto; position: relative;`}
              className={hasScrollbarAlways}>{MouseSelector}</div>} />
        )
      }
      <div css={css`margin: 5px 0 5px 0; flex: auto; position: relative; min-height: 30px;`}>
        <Card elevation={1}
          css={css`position: absolute; width: 50%; height: 100%; padding: 0; display: flex; flex-direction: column;`}>
          <Tooltip
            content={<div>There are some symbols that cannot be rendered in the Visual Editor(such as Matrix)<br />And
              sometimes the Visual Editor is inconvenient because it changes the value of the Text Editor<br />In this
              case, turn this off and use only the Text Editor<br />You can still use the Mouse Selector in the Text
              Editor</div>}>
            <>
              <span
                css={css`font-weight: bold; margin-top: 4px; margin-left: 8px; margin-right: 8px;`}>Visual Editor</span>
              <Switch inline label={t('on')} checked={useMathQuill} onChange={() => {
                if (useMathQuill) {
                  setUseMathQuill(false)
                } else {
                  setUseMathQuill(true)
                  MathQuillRef.current.mathField.latex(contentData || '')
                }
              }} />
            </>
          </Tooltip>
          <div css={css`flex: auto; position: relative; width: 100%;`} onFocusCapture={() => {
            source.current = 'visual'
          }}>
            {mathQuill}</div>
        </Card>
        <Card elevation={1} className={hasScrollbarAlways}
          css={css`position: absolute; left: calc(50% + 2px); width: calc(50% - 2px); height: 100%; padding: 0; overflow: auto;`}>
          <span css={css`font-weight: bold; margin-top: 4px; margin-left: 8px;`}>Preview</span>
          <br />
          <MathRenderer value={contentData || ''} />
        </Card>
      </div>
      <div css={css`display: flex; flex: auto;`}>
        <div css={css`flex: auto; position: relative; overflow: auto;`} className={hasScrollbarAlways}>
          <span css={css`font-weight: bold; margin-top: 4px; margin-left: 8px;`}>Text Editor</span>
          <br />
          {textEditor}</div>
        <Button icon={'clipboard'} title={t('Copy to clipboard')} minimal onClick={() => {
          copyTextToClipBoard(contentData)
          showToastByMenuDirectionBase({
            message: i18next.t('LaTeXCopied')
          })
        }} />
      </div>
    </div>
  )
}

const EditorMini = ({ contentData, setContentData }) => (
  EditorBase(false, { contentData, setContentData })
)
const EditorBig = ({ contentData, setContentData }) => (
  EditorBase(true, { contentData, setContentData })
)

// -----------------------------------------------------------------------------------------------------------

function Render ({ contentData }) {
  // TODO 여기서 block math가 제대로 안되면 앞에다가 \n 같은거 하나 추가하는거
  return (<div css={css`min-width: 200px; min-height: 100px; font-size: 15px;`}><MathRenderer value={contentData || ''} /></div>)
}

const standardHeight = 60
function ThumbNailBase ({ contentData }) {
  // const [containerWidth, setContainerWidth] = useState(0)

  // 이 thumbnail의 container는 position: relative 같은 거여서 position: absolute의 기준이 될 수 있어야 함
  return (
    <React.Fragment>
      {/* <ResizeSensor onResize={(entries) => { */}
      {/*  const width = entries[0].contentRect.width */}
      {/*  // const height = e.contentRect.height */}
      {/*  setContainerWidth(width) */}
      {/*  // setContainerHeight(height) */}
      {/* }}> */}
      {/*  <div css={css`position: absolute; width: 80%; height: 0; appearance: none;`} /> */}
      {/* </ResizeSensor> */}
      <Card css={css`padding: 0; margin: 1px !important;
      max-height: ${standardHeight}px;
      min-width: ${standardHeight / 4}px;
      max-width: ${standardHeight * 4}px;
      overflow: hidden !important; font-weight: normal; font-size: 15px;
    `} interactive>
        <MathRenderer
          value={contentData ? truncate(contentData, { length: 300, separator: '\\', omission: '' }) : ''}
        />
      </Card>
    </React.Fragment>
  )
}

const ForLaTeX = new InsertFormClass({
  typeName: 'LaTeX',
  EditorMini,
  Render,
  ThumbNailBase,
  EditorBig
})

export default ForLaTeX
